import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/Pagination";
import FRAUD_TYPES from "../../config/fraudTypes";

const INDIA_STATES = [
  "all","Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh",
  "Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala",
  "Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland",
  "Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura",
  "Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Jammu and Kashmir","Ladakh"
];

const TABS = [
  { key: "pending",      label: "⏳ Pending",      color: "#d97706", bg: "#fef3c7" },
  { key: "approved",     label: "✅ Approved",     color: "#16a34a", bg: "#dcfce7" },
  { key: "rejected",     label: "❌ Rejected",     color: "#dc2626", bg: "#fee2e2" },
  { key: "under_review", label: "🔍 Under Review", color: "#0891b2", bg: "#ecfeff" },
  { key: "all",          label: "📋 All",          color: "#4318FF", bg: "#ede9fe" },
];

const AdminFrauds = () => {
  const navigate = useNavigate();

  const [frauds,     setFrauds]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [tabCounts,  setTabCounts]  = useState({});
  const LIMIT = 10;

  const [tab,       setTab]       = useState("pending");
  const [search,    setSearch]    = useState("");
  const [fraudType, setFraudType] = useState("all");
  const [state,     setState]     = useState("all");
  const [dateFrom,  setDateFrom]  = useState("");
  const [dateTo,    setDateTo]    = useState("");
  const [exporting,    setExporting]    = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const fetchFrauds = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage, limit: LIMIT,
        ...(tab !== "all"        && { status: tab }),
        ...(search               && { search }),
        ...(fraudType !== "all"  && { fraudType }),
        ...(state !== "all"      && { state }),
        ...(dateFrom             && { dateFrom }),
        ...(dateTo               && { dateTo }),
      });

      const res = await axios.get(
        `${API_BASE_URL}/api/admin/frauds?${params}`,
        { headers: adminAuthHeader() }
      );

      setFrauds(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
      setPage(res.data.currentPage || 1);

      // Fetch counts for all tabs
      if (res.data.counts) {
        setTabCounts(res.data.counts);
      } else {
        // Fetch counts separately
        fetchCounts();
      }
    } catch (err) {
      console.error("Failed to fetch frauds:", err);
      setFrauds([]);
    }
    setLoading(false);
  }, [tab, search, fraudType, state, dateFrom, dateTo]);

  const fetchCounts = async () => {
    try {
      const statuses = ["pending", "approved", "rejected", "under_review"];
      const counts = {};
      await Promise.all(statuses.map(async (s) => {
        const res = await axios.get(
          `${API_BASE_URL}/api/admin/frauds?status=${s}&limit=1&page=1`,
          { headers: adminAuthHeader() }
        );
        counts[s] = res.data.total || 0;
      }));
      setTabCounts(counts);
    } catch {}
  };

  useEffect(() => { setPage(1); fetchFrauds(1); }, [fetchFrauds]);
  useEffect(() => { fetchCounts(); }, []);

  const handleReset = () => {
    setSearch(""); setFraudType("all");
    setState("all"); setDateFrom(""); setDateTo("");
  };

  const buildParams = () => new URLSearchParams({
    ...(tab !== "all"       && { status: tab }),
    ...(search              && { search }),
    ...(fraudType !== "all" && { fraudType }),
    ...(state !== "all"     && { state }),
    ...(dateFrom            && { dateFrom }),
    ...(dateTo              && { dateTo }),
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/frauds/export-csv?${buildParams()}`, { headers: adminAuthHeader() });
      if (!res.ok) { alert("Export failed"); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `fraud-reports-${tab}-${new Date().toISOString().slice(0,10)}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { alert("Export failed"); }
    setExporting(false);
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/admin/frauds/export-csv?${buildParams()}`, { headers: adminAuthHeader() });
      if (!res.ok) { alert("Export failed"); return; }
      const text = await res.text();
      const rows = text.trim().split("\n").map(r =>
        r.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(c => c.replace(/^"|"$/g, "").replace(/""/g, '"'))
      );
      const headers = rows[0];
      const data    = rows.slice(1);
      const win = window.open("", "_blank");
      win.document.write(`<!DOCTYPE html><html><head><title>Fraud Reports</title>
        <style>
          body{font-family:Arial,sans-serif;padding:20px}
          h2{color:#1b2559;margin-bottom:4px}
          p.sub{color:#6b7280;font-size:12px;margin-bottom:16px}
          table{border-collapse:collapse;width:100%;font-size:11px}
          th{background:#4318ff;color:#fff;padding:8px 6px;text-align:left;white-space:nowrap}
          td{padding:6px 6px;border-bottom:1px solid #e0e5f2;vertical-align:top}
          tr:nth-child(even) td{background:#f9faff}
          .badge{display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:700}
          .approved{background:#dcfce7;color:#166534}
          .pending{background:#fef9c3;color:#854d0e}
          .rejected{background:#fee2e2;color:#991b1b}
          .under_review{background:#e0f2fe;color:#075985}
          @media print{button{display:none}}
        </style></head><body>
        <h2>📊 Fraud Reports — ${tab.toUpperCase()}</h2>
        <p class="sub">Generated: ${new Date().toLocaleString("en-IN")} | Total: ${data.length} records</p>
        <button onclick="window.print()" style="margin-bottom:16px;padding:8px 20px;background:#4318ff;color:#fff;border:none;border-radius:8px;cursor:pointer;font-size:13px;">🖨️ Print / Save as PDF</button>
        <table><thead><tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr></thead>
        <tbody>${data.map(row => `<tr>${row.map((cell, ci) => {
          if (ci === 9) { const cls = cell.toLowerCase().replace(" ", "_"); return `<td><span class="badge ${cls}">${cell}</span></td>`; }
          return `<td>${cell}</td>`;
        }).join("")}</tr>`).join("")}</tbody></table>
        </body></html>`);
      win.document.close();
    } catch { alert("PDF export failed"); }
    setExportingPdf(false);
  };

  const approveFraud = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/frauds/${id}/approve`, {}, { headers: adminAuthHeader() });
      fetchFrauds(page); fetchCounts();
    } catch (err) { console.error("Approve failed:", err); }
  };

  const rejectFraud = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/frauds/${id}/reject`, {}, { headers: adminAuthHeader() });
      fetchFrauds(page); fetchCounts();
    } catch (err) { console.error("Reject failed:", err); }
  };

  const currentTab = TABS.find(t => t.key === tab);
  const activeFilterCount = [search, fraudType !== "all", state !== "all", dateFrom, dateTo].filter(Boolean).length;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p className="admin-welcome">Fraud Management</p>
          <h2 className="admin-title">
            {currentTab?.label} Reports
            {total > 0 && <span style={{ fontSize: 14, fontWeight: 500, color: "#a3aed0", marginLeft: 10 }}>({total})</span>}
          </h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={handleExport} disabled={exporting || total === 0}
            style={{ padding: "10px 18px", background: exporting ? "#a3aed0" : "#16a34a", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            📥 {exporting ? "Exporting…" : `Export CSV (${total})`}
          </button>
          <button onClick={handleExportPdf} disabled={exportingPdf || total === 0}
            style={{ padding: "10px 18px", background: exportingPdf ? "#a3aed0" : "#dc2626", color: "#fff", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            🖨️ {exportingPdf ? "Preparing…" : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Tab counts */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {TABS.filter(t => t.key !== "all").map(t => (
          <div key={t.key} onClick={() => { setTab(t.key); setPage(1); }}
            style={{ background: t.bg, borderRadius: 10, padding: "10px 16px", minWidth: 90, textAlign: "center", cursor: "pointer",
              border: `2px solid ${tab === t.key ? t.color : "transparent"}` }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.color }}>{tabCounts[t.key] ?? "—"}</div>
            <div style={{ fontSize: 11, color: t.color, fontWeight: 600 }}>
              {t.key === "under_review" ? "Under Review" : t.key.charAt(0).toUpperCase() + t.key.slice(1)}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderBottom: "2px solid #e0e5f2" }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); setPage(1); handleReset(); }}
            style={{
              padding: "10px 16px", border: "none", cursor: "pointer", background: "none",
              fontSize: 13, fontWeight: 700,
              color: tab === t.key ? t.color : "#a3aed0",
              borderBottom: tab === t.key ? `3px solid ${t.color}` : "3px solid transparent",
              marginBottom: "-2px", transition: "all 0.2s",
            }}>
            {t.label}
            <span style={{ marginLeft: 5, fontSize: 11, background: tab === t.key ? t.bg : "#f4f7fe",
              color: tab === t.key ? t.color : "#a3aed0", padding: "2px 7px", borderRadius: 20, fontWeight: 700 }}>
              {t.key === "all" ? (Object.values(tabCounts).reduce((a, b) => a + b, 0) || "—") : (tabCounts[t.key] ?? "—")}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="admin-card" style={{ marginBottom: 16, padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
            <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#a3aed0" }}>🔍</span>
            <input type="text" placeholder="Search name, phone, DL, Aadhaar, city…"
              value={search} onChange={e => setSearch(e.target.value)} style={inputStyle} />
          </div>
          <select value={fraudType} onChange={e => setFraudType(e.target.value)} style={selectStyle}>
            <option value="all">All Fraud Types</option>
            {FRAUD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={state} onChange={e => setState(e.target.value)} style={selectStyle}>
            {INDIA_STATES.map(s => <option key={s} value={s}>{s === "all" ? "All States" : s}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12, color: "#a3aed0" }}>From:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...selectStyle, width: 150 }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 12, color: "#a3aed0" }}>To:</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ ...selectStyle, width: 150 }} />
          </div>
          {activeFilterCount > 0 && (
            <button onClick={handleReset} style={resetBtnStyle}>✕ Clear ({activeFilterCount})</button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="admin-card">
        {loading ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>Loading…
          </div>
        ) : frauds.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#a3aed0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>{currentTab?.label.split(" ")[0]}</div>
            <p style={{ fontWeight: 600, color: "#1b2559" }}>No {tab === "all" ? "" : tab} reports found</p>
            {activeFilterCount > 0 && <button onClick={handleReset} style={{ ...resetBtnStyle, marginTop: 12 }}>Clear filters</button>}
          </div>
        ) : (
          <>
            <div className="admin-fraud-table">
              {frauds.map(fraud => (
                <div key={fraud._id} className="admin-fraud-item">
                  <div className="fraud-left">
                    <h4>{fraud.fullName}</h4>
                    <p>
                      {fraud.fraudType}
                      {fraud.fraudCity  && ` · ${fraud.fraudCity}`}
                      {fraud.fraudState && `, ${fraud.fraudState}`}
                    </p>
                    <p style={{ fontSize: 12, color: "#a3aed0" }}>
                      📞 {fraud.phoneNumbers?.join(", ") || "—"}
                      {fraud.drivingLicense && ` · DL: ${fraud.drivingLicense}`}
                    </p>
                    <span className="fraud-date">
                      {new Date(fraud.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <div className="fraud-right">
                    <span className={`status ${fraud.status}`}>{fraud.status}</span>
                    {fraud.status === "pending" && (
                      <>
                        <button className="approve-btn" onClick={() => approveFraud(fraud._id)}>✓ Approve</button>
                        <button className="reject-btn"  onClick={() => rejectFraud(fraud._id)}>✕ Reject</button>
                      </>
                    )}
                    {fraud.status === "rejected" && (
                      <button className="approve-btn" onClick={() => approveFraud(fraud._id)}>✓ Approve</button>
                    )}
                    <button className="view-btn" onClick={() => navigate(`/admin/frauds/${fraud._id}`)}>View →</button>
                  </div>
                </div>
              ))}
            </div>
            <Pagination currentPage={page} totalPages={totalPages} total={total} limit={LIMIT} onPageChange={(p) => { setPage(p); fetchFrauds(p); }} />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

const inputStyle = { width: "100%", height: 42, padding: "0 14px 0 36px", borderRadius: 10, border: "1px solid #e0e5f2", fontSize: 13, outline: "none", background: "#f9faff", color: "#1b2559" };
const selectStyle = { height: 42, padding: "0 14px", borderRadius: 10, border: "1px solid #e0e5f2", fontSize: 13, outline: "none", background: "#f9faff", color: "#1b2559", cursor: "pointer" };
const resetBtnStyle = { height: 38, padding: "0 16px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer" };

export default AdminFrauds;